/**
 * @file 收集页面的 fps (Frame per second)
 * @author <caierwei@baijia.com>
 * @module module/fps
 */
/* eslint-disable camelcase */
/* eslint-disable class-methods-use-this */
import configurable from './util/configurable';
import request from './util/request';
import throttle from './util/throttle';
import onRouteChange from './util/onRouteChange';
import warnForbiddenKeys from './util/forbiddenWords';
import metric from './metric';

import {
    trackID,
    getUserID,
    Config,
} from './util/config';

import {
    camelToUnder
} from './util/json';

import {
    on,
    off
} from './util/dom';

import getPath from './util/getPath';

const win = typeof window !== 'undefined' && window;
const doc = win && document;

const rAF = win.requestAnimationFrame || function raf(callback) {
    win.setTimeout(callback, 1000 / 60);
};

const cAF = win.cancelAnimationFrame || function caf(id) {
    win.clearTimeout(id);
};

// 当前浏览器支持的 hidden 和 visibilityChange 写法
const [hidden, visibilityChange] = [
    ['hidden', 'visibilitychange'],
    ['webkitHidden', 'webkitvisibilitychange'],
    ['mozHidden', 'mozvisibilitychange'],
    ['msHidden', 'msvisibilitychange']
].filter(([key]) => key in (doc || {}))[0] || [];

/**
 * 卡顿阈值，默认 20
 *
 * @const {number}
 */
const JANK_FPS = 20;

/**
 * 卡顿事件上报 fps 触发数，默认为 fps 连续 3 次低于 20 即触发上报事件
 *
 * @const {number}
 */
const JANK_COUNT = 3;

/**
 * 用户行为跟踪最大量
 *
 * @const {number}
 */
const USER_TRACK_QUEUE_LIMIT = 10;

/**
 * 两帧之间间隔时长最大限制
 *
 * @const {number}
 */
const INTERVAL_TIME_LIMIT = 116;

/**
  * fps性能上报
  *
  * @function
  * @param {Object} json 要上报的数据
  * @param {Function=} callback 上报后的回调
  */
const report = configurable((json, callback) => {
    const {
        url = location.href,
        uuid = trackID,
        uid = getUserID(),
        path = getPath(),
        fps_list = [],
        user_track = [],
        values = {},
        ...rest
    } = camelToUnder(json);

    // 要上报的数据
    const repData = {
        // 当前页面 URL
        url,

        // 用户的唯一标识，由后端生成写到 cookie 中
        uuid,

        // 登录用户 ID，用户登录后端写到 cookie 中
        uid,

        // 定义的当前页面路径，以区分多个页面
        path,

        fps_list: JSON.stringify(fps_list),
        user_track: JSON.stringify(user_track),
        values: JSON.stringify(values),
        ...rest
    };
    warnForbiddenKeys(repData);
    metric({
        ...repData,
        metric: 'fps'
    });
    request(Config.VERB)(`${Config.PERF}`, repData, callback);
}, {});

class UserTrackQueue {
    queues = [];

    limit = USER_TRACK_QUEUE_LIMIT;

    static getInstance() {
        if (!UserTrackQueue.instance) {
            UserTrackQueue.instance = new UserTrackQueue();
        }
        return UserTrackQueue.instance;
    }

    /**
     * 获取户行为信息
     *
     * @function getQueues
     * @param {number} 取值
     * @return {Array} 返回用户行为信息
     */
    getQueues(...args) {
        let len = args.length > 0 && void 0 !== args[0] ? args[0] : 0;
        len = Math.abs(len) > this.queues.length ? 0 : len;
        return this.queues.slice(len);
    }

    /**
     * 收集用行为信息
     *
     * @function addQueues
     * @param {Object} info 用户行为
     */
    addQueues(info) {
        info && Object.keys(info).length >= 1 && this.queues.push(info);
        if (this.getLength() > this.limit) {
            this.queues.shift();
        }
    }

    /**
     * 获取队列长度
     *
     * @function getLength
     * @return {number} 返回队列长度
     */
    getLength() {
        return this.queues.length;
    }

    /**
     * 获取用户点击路径
     *
     * @function getUserClickTrack
     * @param {Event} click 事件 event
     * @return {Object} 返回路径信息
     */
    getUserClickTrack(e) {
        if (!e) {
            return {};
        }
        const trackInfo = {};
        const node = e.target || e.srcElement;
        trackInfo.tn = node.tagName ? node.tagName.toLowerCase() : '';
        trackInfo.nt = node.nodeType || 0;
        trackInfo.xp = this.getElePath(node);
        trackInfo.id = node.id || '';
        trackInfo.cn = node.className || '';
        trackInfo.url = location.href;
        trackInfo.tm = Date.now();
        trackInfo.type = 'click';
        trackInfo.pos = (function pos(e) {
            const t = {};
            let left = e.offsetLeft || 0;
            let top = e.offsetTop || 0;
            let parent = e.offsetParent;
            while (parent) {
                left += parent.offsetLeft || 0;
                top += parent.offsetTop || 0;
                parent = parent.offsetParent;
            }
            t.x = left;
            t.y = top;
            return t;
        }(node));
        return trackInfo;
    }

    getElePath(node) {
        if (!(node instanceof Element)) {
            return '';
        }
        if (node.id) {
            return `id("${node.id}")`;
        }
        if (node === document.body) {
            return node.tagName.toLowerCase();
        }
        let node_nth = 0;
        const childNodes = node.parentNode && node.parentNode.childNodes || [];
        for (let i = 0, len = childNodes.length; i < len; i++) {
            const curNode = childNodes[i];
            if (curNode === node) {
                return `${this.getElePath(node.parentNode)}/${node.tagName.toLowerCase()}[${node_nth + 1}]`;
            }
            1 === curNode.nodeType && curNode.tagName === node.tagName && node_nth++;
        }
    }
}

class FPSMonitor {

    timerId = null;

    // 上一秒时间
    lastTime = null;

    // 上一帧的时间
    lastFrameTime = null;

    // 1s 执行了多少帧
    frame = 0;

    fpsQueue = [];

    // 两帧之间间隔超过 116ms 的队列
    intervalTimeQueue = [];

    config = {
        jank_fps: JANK_FPS,

        jank_count: JANK_COUNT,

        jank_interval_time: INTERVAL_TIME_LIMIT
    };

    constructor(args = {}) {
        this.config = {
            ...this.config,
            ...args
        };
    }

    /**
     * 获取 FPSMonitor 实例
     *
     * @function getInstance
     * @param {?Object=} [ {jank_fps: 30, jank_count: 3} ] 自定义配置值
     * @return {Object} 返回 fpsMonitor 实例
     */
    static getInstance(conf = {}) {
        if (!FPSMonitor.instance) {
            FPSMonitor.instance = new FPSMonitor({...conf});
        }
        return FPSMonitor.instance;
    }

    /**
     * 判断是否是低帧
     *
     * @function isLowFPS
     * @param {Array} fpsList 采集的 FPS 值
     * @param {number} below 卡顿的阀值限定值
     * @param {number} number 低于 below 多少次
     * @return {boolean} 返回是否是低帧
     */
    isLowFPS(fpsList, below = 20, number = 3) {
        let count = 0;
        for (let i = 0, len = fpsList.length; i < len; i++) {
            if (fpsList[i]?.fps < below) {
                count++;
            }
            else {
                count = 0;
            }
            if (count >= number) {
                return true;
            }
        }
        return false;
    }

    // 上一秒时间
    lastTime = null;

    // 上一帧的时间
    lastFrameTime = null;

    // 1s 执行了多少帧
    frame = 0;

    fpsQueue = [];

    // 两帧之间间隔超过 116ms 的队列
    intervalTimeQueue = [];

    timerId = null;

    /**
     * 重置计算变量
     */
    resetCalculateVar() {
        this.lastTime = null;
        this.lastFrameTime = null;
        this.frame = 0;
        this.fpsQueue = [];
        this.intervalTimeQueue = [];
    }

    /**
     * 计算帧率
     *
     * @function calculateFps
     */
    calculateFps() {
        const that = this;
        function fpsStep(now) {
            // const now = Date.now();
            that.lastTime = that.lastTime ?? now;
            that.lastFrameTime = that.lastFrameTime ?? now;
            const interval_time = now - that.lastFrameTime;
            that.lastFrameTime = now;
            const fps = interval_time > 0 ? Math.round(1000 / interval_time) : 0;
            if (interval_time >= that.config.jank_interval_time) {
                that.intervalTimeQueue.push({
                    time: Date.now(),
                    fps,
                    // 卡顿时长
                    jank_time: interval_time
                });
            }
            else {
                if (that.intervalTimeQueue.length) {
                    const userTrack = UserTrackQueue.getInstance().getQueues(-5);
                    const intervalTimeQueueLen = that.intervalTimeQueue.length;
                    const fps_avg = that.intervalTimeQueue.reduce((sum, intervalTimeItem = {}) => {
                        sum = sum + intervalTimeItem.fps;
                        return sum;
                    }, 0) / intervalTimeQueueLen;

                    const time_total = that.intervalTimeQueue.reduce((sum, intervalTimeItem = {}) => {
                        sum = sum + intervalTimeItem.jank_time;
                        return sum;
                    }, 0);

                    report({
                        fpsList: that.intervalTimeQueue,
                        userTrack,
                        values: {
                            fps_avg,
                            time_total,
                            time_avg: time_total / intervalTimeQueueLen
                        }
                    });
                    that.resetCalculateVar();
                }
                that.frame += 1;
                if (that.lastTime && (now > 1000 + that.lastTime)) {
                    const fps = Math.round(that.frame / ((now - that.lastTime) / 1000));
                    // 不大于设置的 fps 就被认定为低帧率
                    if (fps <= that.config.jank_fps) {
                        that.fpsQueue.push({
                            time: Date.now(),
                            fps
                        });
                    }
                    // 不是低帧率了，就检查之前的低帧是否符合条件
                    else if (that.isLowFPS(that.fpsQueue, that.config.jank_fps, that.config.jank_count)) {
                        const userTrack = UserTrackQueue.getInstance().getQueues(-5);
                        const fpsQueueLen = that.fpsQueue.length;
                        const total_fps = that.fpsQueue.reduce((sum, fpsItem = {}) => {
                            sum = sum + fpsItem.fps;
                            return sum;
                        }, 0);
                        const fps_avg = total_fps / fpsQueueLen;
                        const time_total = fpsQueueLen * 1000;

                        report({
                            fpsList: that.fpsQueue,
                            userTrack,
                            values: {
                                fps_avg,
                                time_total,
                                time_avg: time_total / total_fps
                            }
                        });
                        that.fpsQueue = [];
                    }
                    else {
                        that.fpsQueue = [];
                    }

                    that.frame = 0;
                    that.lastTime = now;
                }
            }

            that.timerId = rAF(fpsStep);

        }
        that.timerId = rAF(fpsStep);
    }

    /**
     * 开始帧率监控
     *
     * @function startMonitor
     */
    startMonitor() {
        this.stopFpsStepRAF();
        this.resetCalculateVar();
        this.calculateFps();
        this.tearDown = onRouteChange(e => {
            const {isTrusted, habo} = e;
            if (isTrusted || (habo && !isTrusted)) {
                this.stopFpsStepRAF();
                this.resetCalculateVar();
                this.calculateFps();
            }
        });
    }

    /**
     *停止帧率监控
     *
     * @function stopMonitor
     */
    stopMonitor() {
        this.stopFpsStepRAF();
        if (typeof this.tearDown === 'function') {
            this.tearDown();
        }
        this.resetCalculateVar();
    }

    /**
     *停止帧率计算
     *
     * @function stopFpsStepRAF
     */
    stopFpsStepRAF() {
        if (this.timerId) {
            cAF(this.timerId);
            this.timerId = null;
        }
    }
}

const observeClick = throttle(e => {
    const trackInfo = UserTrackQueue.getInstance().getUserClickTrack(e);
    UserTrackQueue.getInstance().addQueues(trackInfo);
}, 300);

const monitorFps = () => {
    if (doc[hidden]) {
        FPSMonitor.getInstance().stopMonitor();
    }
    else {
        FPSMonitor.getInstance().startMonitor();
    }
};


/**
 * 解除相关事件的绑定
*/
function destroy() {
    FPSMonitor.getInstance().stopMonitor();
    off(doc, 'click', observeClick, true);
    off(win, 'unload', destroy);
    off(doc, visibilityChange, monitorFps);
}

report.init = (conf = {}) => {
    FPSMonitor.getInstance(conf).startMonitor();
    on(doc, 'click', observeClick, true);
    on(win, 'unload', destroy);
    on(doc, visibilityChange, monitorFps);
};

export default report;
